/**
 * Workflow Designer API client (P2-S6).
 * Mirror libs/workflows/graph.py + apps/auth/api/admin/workflows.py.
 */
import { api } from "./client";

// ============================================================================
// Graph schema
// ============================================================================
export type NodeType =
  | "trigger"
  | "llm"
  | "tool"
  | "form"
  | "branch"
  | "parallel"
  | "human"
  | "notify"
  | "delay"
  | "subflow"
  | "end";

export interface WorkflowNode {
  id: string;
  type: NodeType;
  label?: string;
  config?: Record<string, unknown>;
  position?: { x: number; y: number };
}

export interface WorkflowEdge {
  id?: string;
  from: string;
  to: string;
  label?: string | null;
  condition_label?: string | null;
}

export interface WorkflowGraph {
  code: string;
  title: string;
  description?: string | null;
  entry: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  triggers?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// API DTOs
// ============================================================================
export interface WorkflowMeta {
  id: string;
  code: string;
  name: string;
  description: string | null;
  version: number;
  status: "draft" | "published" | "archived";
  change_notes: string | null;
  published_at: string | null;
  created_by: string;
  created_at: string;
}

export interface WorkflowDetail extends WorkflowMeta {
  graph: WorkflowGraph;
  triggers: Record<string, unknown>;
}

export interface CreateWorkflowBody {
  code: string;
  name: string;
  description?: string | null;
  graph: WorkflowGraph;
  triggers?: Record<string, unknown>;
}

export interface SaveDraftBody {
  graph: WorkflowGraph;
  triggers?: Record<string, unknown>;
  change_notes?: string | null;
}

export interface ValidateResponse {
  is_valid: boolean;
  errors: string[];
}

export interface TestRunBody {
  initial_data?: Record<string, unknown>;
  version?: number | null;
  user_inputs?: Record<string, unknown>;
  max_steps?: number;
}

export interface TestRunResponse {
  status: string;
  current_node: string | null;
  final_data: Record<string, unknown>;
  history: Array<{
    node_id: string;
    type: string;
    logs?: string[];
    branch_label?: string | null;
    error?: string | null;
    kind?: string;
  }>;
  error: string | null;
  pause_payload: Record<string, unknown> | null;
  emitted_widgets: Array<Record<string, unknown>>;
}

export const workflowsApi = {
  list: (includeArchived = false) =>
    api.get<WorkflowMeta[]>("/api/v1/admin/workflows", {
      query: { include_archived: includeArchived },
    }),
  create: (body: CreateWorkflowBody) =>
    api.post<WorkflowMeta>("/api/v1/admin/workflows", body),
  listVersions: (code: string) =>
    api.get<WorkflowMeta[]>(`/api/v1/admin/workflows/${code}`),
  getVersion: (code: string, version: number) =>
    api.get<WorkflowDetail>(`/api/v1/admin/workflows/${code}/versions/${version}`),
  saveDraft: (code: string, body: SaveDraftBody) =>
    api.post<WorkflowMeta>(`/api/v1/admin/workflows/${code}/versions`, body),
  publish: (code: string, version: number) =>
    api.post<WorkflowMeta>(
      `/api/v1/admin/workflows/${code}/versions/${version}/publish`,
    ),
  archive: (code: string) =>
    api.delete<void>(`/api/v1/admin/workflows/${code}`),
  validate: (code: string, body: SaveDraftBody) =>
    api.post<ValidateResponse>(`/api/v1/admin/workflows/${code}/validate`, body),
  testRun: (code: string, body: TestRunBody) =>
    api.post<TestRunResponse>(`/api/v1/admin/workflows/${code}/test-run`, body),
};
